import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { VrsRedirectTarget } from 'features/app/store/app/enums/vrsRedirectTarget';
import type { PendingVrsRedirect } from 'features/app/store/app/interfaces/pendingVrsRedirect';
import { clearPendingVrsRedirectTarget } from 'features/app/store/app/appSlice';
import { selectPendingVrsRedirect } from 'features/app/store/app/selectors';
import {
  getVrsRedirectRoute,
  shouldClearPendingVrsRedirectOnArrival,
} from 'features/router/routes';

const assertNever = (value: never): never => {
  throw new Error(`Unhandled VRS redirect: ${String(value)}`);
};

const getTargetPath = (pendingVrsRedirect: PendingVrsRedirect | null) => {
  if (pendingVrsRedirect == null) {
    return null;
  }

  const { target } = pendingVrsRedirect;

  switch (target) {
    case VrsRedirectTarget.InCenterTeamingNotice:
    case VrsRedirectTarget.TeamingLogoutNotice:
      return getVrsRedirectRoute(target);
    default:
      return assertNever(target);
  }
};

export const GlobalRedirectGate = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pendingVrsRedirect = useAppSelector(selectPendingVrsRedirect);
  const targetPath = getTargetPath(pendingVrsRedirect);

  useEffect(() => {
    if (pendingVrsRedirect == null) {
      return;
    }

    if (targetPath == null) {
      dispatch(clearPendingVrsRedirectTarget());
      return;
    }

    const shouldClearOnArrival = shouldClearPendingVrsRedirectOnArrival(
      pendingVrsRedirect.target
    );

    if (shouldClearOnArrival && location.pathname === targetPath) {
      dispatch(clearPendingVrsRedirectTarget());
    }
  }, [dispatch, location.pathname, pendingVrsRedirect, targetPath]);

  if (targetPath != null && location.pathname !== targetPath) {
    return <Navigate replace to={targetPath} />;
  }

  return <Outlet />;
};
